import type { CallRecordListItem } from '@techgitter/contracts';
import type { AvatarColour } from '../../styles/avatarPalette';
import { SYSTEM_AVATAR_COLOR } from '../../styles/avatarPalette';
import { callLogText, formatCallTime } from '../../utils/callFormat';
import { initialsFromName } from './workItemToRow';
import type { LogEntry } from './WorkItemPanel';

/** A log entry with the instant it sorts by. */
export interface TimedLogEntry {
  at: string;
  entry: LogEntry;
}

/**
 * A 3CX call record as a job-log entry. The avatar is whoever took the call;
 * a call nobody here answered has no one, so it gets the system grey, the same
 * as an authorless SM4RT log. The time is a real date and time rather than the
 * log's "2 weeks ago", because when a customer rang is the thing being read.
 */
export function callToLogEntry(
  call: CallRecordListItem,
  colorFor: (ref: { id?: string | null; name?: string | null }) => AvatarColour,
): TimedLogEntry {
  const { text, isFallback } = callLogText(call);
  const who = call.userFullName?.trim() || '';
  return {
    at: call.startTime,
    entry: {
      id: `call-${call.id}`,
      authorInitials: initialsFromName(who || 'SM4RT'),
      authorId: call.userId || who,
      authorColor: who || call.userId ? colorFor({ id: call.userId, name: who }) : SYSTEM_AVATAR_COLOR,
      source: 'sm4rt',
      typeLabel: 'Call',
      formattedTime: formatCallTime(call.startTime),
      text,
      call: {
        href: `/call/${encodeURIComponent(call.id)}`,
        direction: call.direction,
        callKind: call.callKind,
        noSummary: isFallback,
      },
    },
  };
}

/**
 * Newest first across every source. Stable for equal instants, and an entry
 * whose time doesn't parse sorts last rather than poisoning the comparison.
 */
export function interleaveByTime(entries: TimedLogEntry[]): LogEntry[] {
  const ms = (at: string) => {
    const t = new Date(at).getTime();
    return Number.isNaN(t) ? -Infinity : t;
  };
  return entries
    .map((e, i) => ({ e, i, t: ms(e.at) }))
    .sort((a, b) => (b.t === a.t ? a.i - b.i : b.t - a.t))
    .map(({ e }) => e.entry);
}
